import type { FC, ReactNode } from "react";
import {
  Platform,
  Pressable,
  View,
  type PressableStateCallbackType,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import { useTableContext, useTableLayout, useTableSection } from "./TableContext";

export interface TableRowProps {
  /** `TableCell`s. */
  readonly children: ReactNode;
  /** Shade the row while the pointer is over it (web). Implied by `onPress`. */
  readonly hover?: boolean | undefined;
  /** Highlight the row as selected. */
  readonly selected?: boolean | undefined;
  /**
   * Makes the whole row pressable. On iOS and Android the row becomes a single
   * button for screen readers, so its cells must not contain their own buttons
   * or links. Put those in `expandedContent` instead.
   */
  readonly onPress?: (() => void) | undefined;
  /** Whether the row is expanded. Announced to assistive tech and shows `expandedContent`. */
  readonly expanded?: boolean | undefined;
  /**
   * Full-width content shown under the cells while `expanded`, such as a detail
   * panel. It sits outside the pressable area, so it may hold interactive controls.
   */
  readonly expandedContent?: ReactNode;
  readonly accessibilityLabel?: string | undefined;
  /** Style for the row of cells (not the expanded content). */
  readonly style?: StyleProp<ViewStyle> | undefined;
  readonly testID?: string | undefined;
}

/**
 * A row of `TableCell`s, modelled on MUI's `TableRow`.
 *
 * Always renders the same element tree whatever its props, so toggling
 * `onPress` (e.g. while saving) never remounts the cells.
 */
const TableRow: FC<TableRowProps> = ({
  children,
  hover = false,
  selected = false,
  onPress,
  expanded,
  expandedContent,
  accessibilityLabel,
  style,
  testID,
}) => {
  const { size } = useTableContext();
  const section = useTableSection();
  const stacked = useTableLayout() === "stacked";
  const isHead = section === "head";
  const pressable = !isHead && onPress !== undefined;
  const hoverable = !isHead && (pressable || hover);

  // react-native-web passes `hovered` alongside `pressed`; native never hovers.
  const rowStyle = (state: PressableStateCallbackType): StyleProp<ViewStyle> => {
    const hovered = hoverable && "hovered" in state && state.hovered === true;
    const pressed = pressable && state.pressed;
    return [
      styles.cells,
      stacked ? styles.stackedCells
      : isHead ? styles.headCells
      : size === "small" ? styles.smallCells
      : styles.bodyCells,
      Platform.OS === "web" && (pressable ? styles.webPressable : styles.webStatic),
      selected ?
        hovered || pressed ?
          styles.selectedHovered
        : styles.selected
      : pressed ? styles.pressed
      : hovered && styles.hovered,
      style,
    ];
  };

  return (
    <View style={styles.row}>
      <Pressable
        // On native a pressable row is announced as a button; `row` has no
        // native trait, so it would read as plain text.
        role={pressable && Platform.OS !== "web" ? "button" : "row"}
        // Pressable makes itself one accessible element on native, which would
        // hide any controls in the cells. Only a pressable row should group them.
        accessible={Platform.OS === "web" ? undefined : pressable}
        tabIndex={pressable ? 0 : -1}
        aria-label={accessibilityLabel}
        aria-selected={selected || undefined}
        aria-expanded={expanded}
        testID={testID}
        onPress={onPress}
        style={rowStyle}
      >
        {children}
      </Pressable>
      {expanded === true && expandedContent !== undefined && (
        <View role="row" style={styles.expanded}>
          <View role="cell" style={[styles.expandedCell, stacked && styles.stackedExpandedCell]}>
            {expandedContent}
          </View>
        </View>
      )}
    </View>
  );
};

TableRow.displayName = "TableRow";

export { TableRow };

const styles = StyleSheet.create((theme) => ({
  row: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.borderCard,
  },
  cells: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  headCells: {
    minHeight: 40,
  },
  bodyCells: {
    minHeight: 52,
  },
  smallCells: {
    minHeight: 36,
  },
  // Cells drop their own padding in the stacked layout; the row pads them as
  // one block instead.
  stackedCells: {
    flexDirection: "column",
    gap: theme.spacing.sm,
    paddingHorizontal: theme.spacing["3xl"],
    paddingVertical: theme.spacing.xl,
  },
  hovered: {
    backgroundColor: theme.colors.table.rowHover,
  },
  pressed: {
    backgroundColor: theme.colors.table.rowPressed,
  },
  selected: {
    backgroundColor: theme.colors.table.rowSelected,
  },
  selectedHovered: {
    backgroundColor: theme.colors.table.rowSelectedHover,
  },
  expanded: {
    flexDirection: "row",
  },
  expandedCell: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: theme.spacing["3xl"],
    paddingBottom: theme.spacing["3xl"],
  },
  stackedExpandedCell: {
    paddingBottom: theme.spacing.xl,
  },
  webPressable: Object.assign({ cursor: "pointer" } satisfies ViewStyle, {
    transition: "background-color 0.15s ease-in-out",
  }),
  // Pressable defaults to a pointer cursor on web, even without `onPress`.
  webStatic: Object.assign({ cursor: "auto" } satisfies ViewStyle, {
    transition: "background-color 0.15s ease-in-out",
  }),
}));
